//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { jwtDecode, JwtPayload } from 'jwt-decode';
import isequal from 'lodash.isequal';
import { User } from 'oidc-client-ts';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useConfig } from './Config';

export interface UserAuthContextType {
  isLoading: boolean;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  user: User;
  roles: string[];
  hasBasicRole: boolean;
  licenses: string[];
  feature: string | null;
  licenseErrorCode?: number | undefined;
}

interface CustomJwtPayload extends JwtPayload {
  name: string | null;
  exp: number;
  realm_access?: {
    roles: string[];
  };
}

export const BASIC_ROLES = ['ROLE_USER', 'ROLE_ADMIN'];

const UserAuthContext = createContext<UserAuthContextType | undefined>(
  undefined,
);

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserAuthProvider: React.FC<UserProviderProps> = ({ children }) => {
  const auth = useAuth();
  const { services } = useConfig();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User>({} as User);
  const isAuthenticating = auth.isLoading;
  const isAuthenticated = auth.isAuthenticated;
  const [roles, setRoles] = useState<string[]>([]);
  const [hasBasicRole, setHasBasicRole] = useState<boolean>(false);
  const [licenses, setLicenses] = useState<string[]>([]);
  const [feature, setFeature] = useState<string | null>(null);
  const [licenseErrorCode, setLicenseErrorCode] = useState<number | undefined>(
    undefined,
  );

  const [licensesLoaded, setLicensesLoaded] = useState(false);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const licensesAlreadyLoaded = useRef(false);

  useEffect(() => {
    if (auth.user) {
      setUser(auth.user);
      setIsUserLoaded(true);
    }
  }, [auth.user]);

  useEffect(() => {
    if (auth.user?.access_token) {
      const decodedToken = jwtDecode<CustomJwtPayload>(auth.user.access_token);
      const userRoles = decodedToken.realm_access?.roles || [];

      if (!isequal(userRoles, roles)) {
        setRoles(userRoles);
      }

      const hasRoles = BASIC_ROLES.some((role) => userRoles.includes(role));
      if (hasRoles && !hasBasicRole) {
        setHasBasicRole(hasRoles);
      }
      setRolesLoaded(true);
    }
  }, [auth.user?.access_token]);

  const fetchLicenses = async () => {
    const accessToken = user.access_token;

    if (!accessToken) {
      //The access token is not yet available;
      return;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        // Mock response for local development
        const mockResponse = {
          feature: 'nexus-t3',
          userId: 'manan_singh@hcl.com',
          expiration: '2025-06-30T00:00:00Z',
          products: [
            'plan',
            'control',
            'code',
            'test',
            'deploy',
            'build',
            'measure',
            'release',
            'model',
          ],
          checkoutTime: '2025-04-28T08:05:51.020789143Z',
        };
        setLicenses(mockResponse.products);
        setFeature(mockResponse.feature);
      } else {
        const url = services?.license?.url + '/license';
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status !== 200) {
          setLicenseErrorCode(response.status);
        } else {
          const reader = response.body?.getReader();
          if (reader) {
            const decoder = new TextDecoder();
            let done = false;
            let fullText = '';

            while (!done) {
              const { value, done: streamDone } = await reader.read();
              done = streamDone;
              if (value) {
                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;
              }
            }

            // Parse the JSON response
            const parsedResponse = JSON.parse(fullText);
            const productsArray = parsedResponse.products || [];
            const featureValue = parsedResponse.feature || null;

            // Set licenses and feature with the extracted values
            setLicenses(productsArray);
            setFeature(featureValue);
          }
        }
      }
    } catch (error: any) {
      // TODO This should direct to an error page, but there is no
      // generic error page yet. Stories are logged to create one.
      console.error('Error calling licensing server', error);
      // If fetch threw due to network/connection, treat as 503 analogue.
      if (licenseErrorCode === undefined) {
        setLicenseErrorCode(503);
      }
    } finally {
      licensesAlreadyLoaded.current = true;
      setLicensesLoaded(true);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [user.access_token]);

  // Periodically refresh license every 5 minutes
  useEffect(() => {
    if (!user.access_token) return;
    const interval = setInterval(
      () => {
        fetchLicenses();
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [user.access_token]);

  useEffect(() => {
    if (isUserLoaded && rolesLoaded && licensesLoaded) {
      setIsLoading(false);
    }
  }, [isUserLoaded, rolesLoaded, licensesLoaded]);

  if (!auth) {
    return null;
  }

  return (
    <UserAuthContext.Provider
      value={{
        isLoading,
        isAuthenticating,
        isAuthenticated,
        user,
        roles,
        licenses,
        hasBasicRole,
        feature,
        licenseErrorCode,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export { UserAuthContext };
